import {
    canAccessMultiManagerNav,
    CLINIC_HOME_PATH,
    CLINIC_SUBSCRIPTION_PATH,
    isBillingActivationPending,
    isClinicManager,
} from "@/domains/multi/clinicMemberPaths";
import { Navigate, Outlet, useLocation } from "react-router";
import { ClinicRoles } from "@/domains/multi/clinicRoles";
import type { ClinicRole } from "@/domains/multi/clinicTypes";
import { useAuth } from "@/domains/shared/hooks/useAuth";
import { resolveClinicalAccessBlockedReason } from "@/domains/subscription/accountAccess";

type ClinicRoleRouteGuardProps = {
    roles: ClinicRole[];
};

export default function ClinicRoleRouteGuard({ roles }: ClinicRoleRouteGuardProps) {
    const { user, loading } = useAuth();
    const location = useLocation();
    const normalizedPath = location.pathname.replace(/\/+$/, "");

    if (loading) {
        return (
            <div className="min-h-[40vh] flex items-center justify-center">
                <div className="h-10 w-10 border-2 border-border border-t-primary rounded-full animate-spin" />
            </div>
        );
    }

    const orgBillingBlocked = resolveClinicalAccessBlockedReason(user) === "org_billing_blocked";
    if (normalizedPath === CLINIC_SUBSCRIPTION_PATH && user?.activeClinicId && orgBillingBlocked) {
        return <Outlet />;
    }

    if (!isClinicManager(user)) {
        return <Navigate to={orgBillingBlocked ? CLINIC_SUBSCRIPTION_PATH : "/profissional"} replace />;
    }

    if (!canAccessMultiManagerNav(user) && normalizedPath !== CLINIC_SUBSCRIPTION_PATH) {
        const target = isBillingActivationPending(user)
            ? `${CLINIC_SUBSCRIPTION_PATH}?checkout=1`
            : CLINIC_SUBSCRIPTION_PATH;
        return <Navigate to={target} replace />;
    }

    const userRoles = user?.clinicRoles ?? [];
    const allowed = roles.some((r) => userRoles.includes(r));
    if (!allowed) {
        if (userRoles.includes(ClinicRoles.Manager)) {
            return <Navigate to={CLINIC_HOME_PATH} replace />;
        }
        return <Navigate to="/profissional" replace />;
    }

    return <Outlet />;
}
